import type { ColumnDef } from "@tanstack/react-table"
import { AnalysisSetBadge, visibleAnalysisSetStatus } from '@/components/deep-analysis/AnalysisSetBadge';
import { DeadlinePopover, type DeadlineTaskStates } from '@/components/correspondence/DeadlinePopover';
import { DocumentActionsDropdown } from '@/components/shared';
import type { Document } from '@/hooks/useDocuments';
import {
  createCorrespondenceSummaryColumn,
  createDateColumn,
  createFileTypeColumn,
  createFromColumn,
  createOpenInNewTabColumn,
  createSelectionColumn,
  createTableCheckboxColumn,
  createTagsColumn,
  createToColumn,
  type SelectionColumnConfig,
} from './column-builders';
import type { DocumentDeleteOptions } from '@/types/document';

export const ColumnsCorrespondence = ({
  refreshData,
  handleDelete,
  slug,
  openDocumentPanel,
  deadlineTaskStates = {},
  deepAnalysisEnabled = false,
}: {
  refreshData: () => void;
  handleDelete: (documentIds: string[], options?: DocumentDeleteOptions) => Promise<void>;
  slug: string | undefined;
  deepAnalysisEnabled?: boolean;
  openDocumentPanel: (params: {
    documentGuid: string;
    citationNumber?: string;
    chunkId?: number;
    activeTab?: 'overview' | 'document';
    attachmentGuid?: string;
  }) => void;
  deadlineTaskStates?: DeadlineTaskStates;
}) => {
  const handleDocumentClick = (documentId: string) => {
    openDocumentPanel({
      documentGuid: documentId,
      activeTab: 'document',
    });
  };

  const linkConfig = { slug, onDocumentClick: handleDocumentClick };

  const columns: ColumnDef<Document>[] = [
    createTableCheckboxColumn(),
    createFileTypeColumn({
      linkConfig,
      showExpandChevron: true,
      iconOverlay: (document) => {
        if (!deepAnalysisEnabled) return null;
        const status = visibleAnalysisSetStatus(document);
        if (!document.analysisSetEffective && !status) return null;
        return (
          <AnalysisSetBadge
            active={document.analysisSetEffective}
            status={status}
            compact
            iconOnly
          />
        );
      },
    }),
    createDateColumn(),
    createFromColumn(),
    createToColumn(),
    createCorrespondenceSummaryColumn({ linkConfig, widthClassName: 'min-w-[360px] xl:min-w-[560px]' }),
    createTagsColumn(),
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex flex-row items-center justify-end gap-1">
          <DeadlinePopover
            deadlines={row.original.deadlines ?? []}
            deadlineTaskStates={deadlineTaskStates}
            slug={slug}
            documentGuid={row.original.guid}
          />
          <DocumentActionsDropdown
            document={row.original}
            slug={slug || ''}
            fromPath="case-files/correspondence"
            onRefresh={refreshData}
            isCorrespondence
            handleDelete={handleDelete}
            deepAnalysisEnabled={deepAnalysisEnabled}
          />
        </div>
      ),
    },
  ];

  // Add meta properties to each column that should use normal click behavior
  columns.forEach(column => {
    if (column.id !== 'id' && column.id !== 'actions' && column.id !== 'expand') {
      column.meta = {
        ...column.meta,
        enableClickToCopy: false,
        enableRowDoubleClick: true
      };
    }
  });

  return columns;
}

/**
 * Selection-mode columns for DocumentSelector component when viewing Correspondence.
 * Uses shared column builders for consistency and reduced duplication.
 * - Uses external selection state (isSelected/onToggleSelection)
 * - Includes From, To columns like main Correspondence page
 * - Includes Tags column
 * - File type icons open documents in new tab when clicked
 * - No Attachments, Actions, or Expand columns
 */
export const ColumnsCorrespondenceSelectionMode = ({
  documents = [],
  ...config
}: SelectionColumnConfig & {
  documents?: Document[];
}): ColumnDef<Document>[] => {
  // Build tag colors map from all documents
  const tagColors: Record<string, string> = {};
  documents.forEach(doc => {
    if (doc.tag_colors) {
      Object.entries(doc.tag_colors).forEach(([tag, color]) => {
        if (color && !tagColors[tag]) {
          tagColors[tag] = color;
        }
      });
    }
  });

  return [
    createSelectionColumn(config),
    createFileTypeColumn({
      isSelectionMode: true,
      matterSlug: config.matterSlug,
      getDocumentStatusIcons: config.getDocumentStatusIcons,
      iconOverlay: config.getDocumentIconOverlay,
    }),
    createDateColumn(),
    createFromColumn(),
    createToColumn(),
    createCorrespondenceSummaryColumn(),
    createTagsColumn({ hideSettings: true, tagColors }),
    createOpenInNewTabColumn(config.matterSlug),
  ];
};
